import { ExitToApp } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  Typography,
} from '@mui/material';
import logoExpandedWhite from 'assets/images/logoProdutos/alertai_icon_full_white.svg';
import logoExpanded from 'assets/images/logoProdutos/alertai_icon_light.svg';
import logoWhite from 'assets/images/logoProdutos/alertai_icon_single_full_white.svg';
import logo from 'assets/images/logoProdutos/alertai_icon_single_light.svg';
import typography from 'assets/theme/base/typography';
import { t } from 'i18next';
import React, { useContext, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { isSportsPlan } from 'services/DefaultService';
import { fontColorDarkTheme } from 'styles/global.styles';
import { AppContext } from '../../context/AppContextProvider';
import { IRouteProps } from '../../routes/routes';
import { isRouteRenderable, SidebarMenuItem } from '../SidebarComponent/_components/SidebarMenuItem';
import { SidebarSectionTitle } from '../SidebarComponent/_components/SidebarSectionTitle';

const ACCENT_COLOR = 'var(--app-accent)';
const ACCENT_BG = 'var(--app-accent-bg)';

interface HeaderComponentProps {
  onMenuClick?: () => void;
  routes: IRouteProps[];
}

const findRouteByPath = (allRoutes: IRouteProps[], path: string): IRouteProps | undefined => {
  for (const route of allRoutes) {
    if (route.route === path) return route;
    if (route.collapse) {
      const nested = findRouteByPath(route.collapse, path);
      if (nested) return nested;
    }
  }
  return undefined;
};

const HeaderComponent: React.FC<HeaderComponentProps> = ({
  onMenuClick,
  routes,
}) => {
  const { size } = typography;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const { isRouteAllowed, theme } = useContext(AppContext);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [touchStartY, setTouchStartY] = useState<number | null>(null);

  const handleToggle = (key: string) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY !== null) {
      const touchEndY = e.changedTouches[0].clientY;
      if (touchStartY - touchEndY > 50) {
        setIsSidebarOpen(false);
      }
    }
    setTouchStartY(null);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    setExpandedKeys({});
  };

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ backgroundColor: 'var(--app-surface-2)' }}
      p={2.0}
    >
      <Box>
        <img
          src={theme === 'BLACK_WHITE' ? logoWhite : logo}
          alt="Logo"
          style={{ width: '17%' }}
        />
      </Box>
      <Box>
        <Typography
          variant="h6"
          sx={{
            color: '#fff',
            mx: 2,
            textTransform: 'capitalize',
          }}
        >
          {t(findRouteByPath(routes, pathname)?.name ?? '')}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center">
        <IconButton onClick={toggleSidebar}>
          <MenuIcon sx={{ color: '#fff' }} />
        </IconButton>
        <Drawer
          anchor="top"
          open={isSidebarOpen}
          onClose={toggleSidebar}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: 'var(--app-surface-2)',
              color: '#fff',
              p: 3,
            },
          }}
        >
          <List>
            <Box
              sx={{
                height: 30,
                flexShrink: 0,
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img
                key="logo-expanded"
                height={30}
                src={theme === 'BLACK_WHITE' ? logoExpandedWhite : logoExpanded}
              />
            </Box>
            {isSportsPlan() && (
              <Box sx={{ textAlign: 'center', mt: 1, flexShrink: 0 }}>
                <Typography
                  component="span"
                  sx={{
                    display: 'inline-block',
                    fontSize: 11,
                    fontWeight: 500,
                    color: ACCENT_COLOR,
                    backgroundColor: ACCENT_BG,
                    borderRadius: 999,
                    px: 1.25,
                    py: 0.25,
                  }}
                >
                  Plano Sports
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Divider
                color={fontColorDarkTheme}
                sx={{ mt: 2, width: '50%' }}
              />
            </Box>
            {routes.map((route, routeIndex) => {
              if (route.type === 'title') {
                const rest = routes.slice(routeIndex + 1);
                const nextTitleOffset = rest.findIndex((r) => r.type === 'title');
                const sectionRoutes = nextTitleOffset === -1 ? rest : rest.slice(0, nextTitleOffset);
                if (!sectionRoutes.some((r) => isRouteRenderable(r, isRouteAllowed))) return null;

                return (
                  <SidebarSectionTitle
                    key={route.key || routeIndex}
                    name={route.name ?? ''}
                    isOpenedSidebar={isSidebarOpen}
                    isFirst={routeIndex === 0}
                  />
                );
              }

              if (!route.protected || route.hideInMenu) {
                return null;
              }

              return (
                <SidebarMenuItem
                  key={route.key || routeIndex}
                  route={route}
                  pathname={pathname}
                  isOpenedSidebar={isSidebarOpen}
                  isExpanded={!!expandedKeys[route.key]}
                  onToggle={() => handleToggle(route.key)}
                  onNavigate={() => setIsSidebarOpen(false)}
                />
              );
            })}
            <Box sx={{ flexShrink: 0 }}>
              <Divider sx={{ my: 2, borderColor: fontColorDarkTheme }} />
              <NavLink to="/app/logout" style={{ textDecoration: 'none' }}>
                <Button
                  variant="text"
                  sx={{
                    color: 'red',
                    width: '100%',
                    borderRadius: 2,
                    p: 1.5,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    transition: 'background-color 0.15s',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                  }}
                  startIcon={<ExitToApp />}
                >
                  <Typography
                    sx={{
                      mr: 'auto',
                      ml: 0,
                      fontSize: 16,
                      whiteSpace: 'nowrap',
                      transition: 'visibility 0s, opacity 0.5s linear',
                    }}
                  >
                    Sair
                  </Typography>
                </Button>
              </NavLink>
            </Box>
          </List>
        </Drawer>
      </Box>
    </Box>
  );
};

export default HeaderComponent;
